//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { ERC20Wei, otAssert } from '@shared/conversions'

import { PoolDynamicAbi } from '@shared/Abis'
import { DynamicWithdrawalActivityCTO } from '@shared/PoolEventCTO'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { PoolDynamic } from '@typechain-types/contracts'

export const repayDynamicWithdrawTx =
  (props: {
    activity: DynamicWithdrawalActivityCTO
    amount: ERC20Wei
    fees: ERC20Wei
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.WITHDRAW_REQUEST

    try {
      const { webSocket, userConnection } = getState().chainState
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      otAssert(userConnection, 'userConnection is not defined')
      const { roleEnum, signer } = userConnection
      assertRole(roleEnum, RoleEnum.POOL_ADMIN)
      const { activity, amount, fees } = props

      logOT.debug('repayEarlyWithdraw', activity.poolAddr)
      await repayDynamicWithdraw(webSocket, signer as Signer, activity, amount, fees, chainConfigurationName)
      dispatch(poolActions.repayDynamicWithdrawSuccess(props))
    } catch (e: any) {
      dispatch(poolActions.repayDynamicWithdrawFail(props))
      console.error('repayEarlyWithdraw error', e)
      await dispatchError(dispatch, e, activity, 1017)
    }
  }

export const repayDynamicWithdraw = async (
  webSocket: WebSocket | undefined,
  adminSigner: Signer,
  activity: DynamicWithdrawalActivityCTO,
  amount: ERC20Wei,
  fees: ERC20Wei,
  chainConfigurationName: string
): Promise<void> => {
  logOT.info('repayWithdraw', activity, amount, fees)

  const { poolAddr, lender } = activity

  const txUuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.WITHDRAW_REQUEST, poolAddr, txUuid, chainConfigurationName))
  const poolContract = new Contract(poolAddr, PoolDynamicAbi, adminSigner) as unknown as PoolDynamic

  logOT.info('repayWithdraw', lender, amount, fees, activity.uuid)

  const tx = await poolContract.repayRedemption(lender, amount, fees, activity.uuid)

  webSocket?.send(
    wsServerActionPoolTX(ActivityEnum.WITHDRAW_REQUEST, poolAddr, txUuid, tx.hash as TxHash, chainConfigurationName)
  )
  await tx.wait(1)

  logOT.debug('repayEarlyWithdraw tx', tx)
}
